import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity,
  LogOut,
  Menu,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  LayoutDashboard,
  Users,
  History,
  Stethoscope,
  UserRound,
  Pill,
  Settings
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import ATHLogo from '../assets/ATHLogo.jpg';

const Sidebar = ({ collapsed = false, onToggleCollapse, width }) => {
  const location = useLocation();
  const { logout, user } = useContext(AuthContext);
  const [mobileOpen, setMobileOpen] = useState(false);

  // States for sub-menus
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [patientOpen, setPatientOpen] = useState(false);
  const [pharmacyOpen, setPharmacyOpen] = useState(false);
  const [pharmaMasterOpen, setPharmaMasterOpen] = useState(false);
  const [pharmaPOOpen, setPharmaPOOpen] = useState(false);

  const handleLogout = () => {
    setMobileOpen(false);
    logout();
  };

  const hasPermission = (path) => {
    if (user?.role === 'Main Admin') return true;
    return user?.permissions?.includes(path);
  };

  const hasAnyPermission = (paths) => {
    if (user?.role === 'Main Admin') return true;
    return paths.some((path) => user?.permissions?.includes(path));
  };

  const doctorPaths = ['/DepartmentList', '/DoctorRegistration', '/DoctoreSchedule'];
  const patientPaths = ['/AppointmentBooking', '/PatientsRegistration', '/ConsultationBilling', '/Prescription'];
  const pharmacyMasterPaths = [
    '/TypeMaster',
    '/UnitMeasurementMaster',
    '/MedicineCategoryMaster',
    '/MedicineMaster',
    '/GenericMaster',
    '/SupplierMaster',
    '/CompanyName'
  ];
  const pharmacyPOPaths = ['/PurchaseOrder', '/PurchaseOrderApproval'];
  const pharmacyPaths = [...pharmacyMasterPaths, ...pharmacyPOPaths, '/GRN', '/Stock', '/PharmacyBilling'];

  const showDoctorMenu = hasAnyPermission(doctorPaths);
  const showPatientMenu = hasAnyPermission(patientPaths);
  const showPharmacyMenu = hasAnyPermission(pharmacyPaths);
  const showPharmacyMasterMenu = hasAnyPermission(pharmacyMasterPaths);
  const showPharmacyPOMenu = hasAnyPermission(pharmacyPOPaths);

  const navLink = (path, name, icon) => {
    if (!hasPermission(path)) return null;
    const isActive = location.pathname === path;
    return (
      <Link
        to={path}
        title={collapsed ? name : undefined}
        onClick={() => setMobileOpen(false)}
        className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} rounded-2xl px-3 py-3 text-[11px] font-bold transition-all ${
          isActive
            ? 'bg-[#2563eb] text-white shadow-lg shadow-[#2563eb]/40'
            : 'text-slate-300 hover:bg-white/10 hover:text-white'
        }`}
      >
        <span className={isActive ? 'text-white' : 'text-slate-300'}>{icon}</span>
        {!collapsed && <span>{name}</span>}
      </Link>
    );
  };

  const subLink = (path, name, isDeep = false) => {
    if (!hasPermission(path)) return null;
    const isActive = location.pathname === path;
    return (
      <Link
        to={path}
        onClick={() => setMobileOpen(false)}
        className={`block py-1.5 text-[11px] font-bold transition-colors ${
          isDeep ? 'ml-16' : 'ml-12'
        } ${isActive ? 'text-[#38bdf8] font-extrabold' : 'text-slate-300 hover:text-white'}`}
      >
        • {name}
      </Link>
    );
  };

  return (
    <>
      {/* MOBILE HEADER */}
      <div className="fixed inset-x-0 top-0 z-50 flex h-14 items-center gap-3 bg-[#1b2559] px-4 shadow-xl lg:hidden border-b border-white/10">
        <button onClick={() => setMobileOpen(true)} className="rounded-lg bg-white/10 p-1.5 text-white">
          <Menu size={20} />
        </button>
        <span className="text-base font-bold tracking-tight text-white">ASHA THE HOPE</span>
      </div>

      {/* SIDEBAR CONTAINER */}
      <div
        className={`
          fixed inset-y-0 left-0 z-[60] flex flex-col border-r border-white/10 bg-[#1b2559] text-slate-100 transition-all duration-300
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        `}
        style={{ width }}
      >
        {/* LOGO & TOGGLE */}
        <div className="border-b border-white/10 p-3">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${collapsed ? 'hidden' : 'gap-3'}`}>
              <div className="p-1 bg-white rounded-xl shadow-md">
                <img src={ATHLogo} alt="Logo" className="h-7 w-auto object-contain" />
              </div>
              {!collapsed && (
                <div>
                  <span className="block text-xs font-black leading-tight tracking-tight text-white">ASHA THE HOPE</span>
                  <span className="block text-[8px] font-bold uppercase tracking-wider text-slate-300">Diagnostic & Imaging</span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onToggleCollapse}
              className={`rounded-lg bg-white/10 p-2 text-white transition hover:bg-white/20 ${collapsed ? 'mx-auto' : ''}`}
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
        </div>

        {/* NAVIGATION */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-2 custom-scrollbar">
          {navLink('/dashboard', 'Dashboard', <LayoutDashboard size={18} />)}

          {/* DOCTOR MANAGEMENT */}
          {!collapsed && showDoctorMenu && (
            <div>
              <button
                onClick={() => setDoctorOpen(!doctorOpen)}
                className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-[11px] font-bold transition-all ${
                  doctorOpen ? 'text-white bg-white/10' : 'text-slate-300 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Stethoscope size={18} className={doctorOpen ? 'text-[#38bdf8]' : 'text-slate-300'} />
                  <span>Doctor Manage</span>
                </div>
                {doctorOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {doctorOpen && (
                <div className="mt-1 space-y-1">
                  {subLink('/DepartmentList', 'Department')}
                  {subLink('/DoctorRegistration', 'Doctor Registration')}
                  {subLink('/DoctoreSchedule', 'Doctor Schedule')}
                </div>
              )}
            </div>
          )}

          {/* PATIENT MANAGEMENT */}
          {!collapsed && showPatientMenu && (
            <div>
              <button
                onClick={() => setPatientOpen(!patientOpen)}
                className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-[11px] font-bold transition-all ${
                  patientOpen ? 'text-white bg-white/10' : 'text-slate-300 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <UserRound size={18} className={patientOpen ? 'text-[#38bdf8]' : 'text-slate-300'} />
                  <span>Patient Manage</span>
                </div>
                {patientOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {patientOpen && (
                <div className="mt-1 space-y-1">
                  {subLink('/AppointmentBooking', 'Appointment Booking')}
                  {subLink('/AppointmentManagement', 'Appointment Manage')}
                  {subLink('/AppointmentPatientsUpdate', 'Appointment Update')}
                  {subLink('/PatientsRegistration', 'Patients Reg')}
                  {subLink('/ConsultationBilling', 'Consulting Billing')}
                  {subLink('/Prescription', 'Prescription')}
                </div>
              )}
            </div>
          )}

          {/* PHARMACY MANAGEMENT */}
          {!collapsed && showPharmacyMenu && (
            <div>
              <button
                onClick={() => setPharmacyOpen(!pharmacyOpen)}
                className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-[11px] font-bold transition-all ${
                  pharmacyOpen ? 'text-white bg-white/10' : 'text-slate-300 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Pill size={18} className={pharmacyOpen ? 'text-[#38bdf8]' : 'text-slate-300'} />
                  <span>Pharmacy</span>
                </div>
                {pharmacyOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              
              {pharmacyOpen && (
                <div className="mt-1 space-y-1">
                  {/* PHARMA MASTER SUB */}
                  {showPharmacyMasterMenu && (
                    <>
                      <button onClick={() => setPharmaMasterOpen(!pharmaMasterOpen)} className="flex w-full items-center justify-between py-1.5 pl-12 pr-4 text-[11px] font-bold text-slate-300 hover:text-white">
                        <span>Master</span>
                        {pharmaMasterOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                      {pharmaMasterOpen && (
                        <div className="space-y-1">
                          {subLink('/TypeMaster', 'Type', true)}
                          {subLink('/UnitMeasurementMaster', 'Unit', true)}
                          {subLink('/MedicineCategoryMaster', 'Medicine Category', true)}
                          {subLink('/GenericMaster', 'Generic', true)}
                          {subLink('/SupplierMaster', 'Supplier', true)}
                          {subLink('/CompanyName', 'Companies', true)}
                          {subLink('/MedicineMaster', 'Medicine Master', true)}
                        </div>
                      )}
                    </>
                  )}

                  {/* PURCHASE ORDER SUB */}
                  {showPharmacyPOMenu && (
                    <>
                      <button onClick={() => setPharmaPOOpen(!pharmaPOOpen)} className="flex w-full items-center justify-between py-1.5 pl-12 pr-4 text-[11px] font-bold text-slate-300 hover:text-white">
                        <span>Purchase Order</span>
                        {pharmaPOOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                      {pharmaPOOpen && (
                        <div className="space-y-1">
                          {subLink('/PurchaseOrder', 'Purchase Order', true)}
                          {subLink('/PurchaseOrderApproval', 'PO Approval', true)}
                        </div>
                      )}
                    </>
                  )}

                  {subLink('/GRN', 'GRN')}
                  {subLink('/Stock', 'Stock Management')}
                  {subLink('/PharmacyBilling', 'Pharmacy Billing')}
                </div>
              )}
            </div>
          )}

          <div className="pt-4 mt-4 border-t border-white/10">
            {navLink('/UserManagement', 'User Management', <Users size={18} />)}
            {navLink('/ActivityHistory', 'Activity History', <History size={18} />)}
          </div>
        </nav>

        {/* LOGOUT */}
        <div className="border-t border-white/10 p-3">
          <button
            onClick={handleLogout}
            className={`flex w-full items-center ${
              collapsed ? 'justify-center' : 'gap-2 px-4'
            } rounded-xl border border-white/10 bg-white/5 py-3 text-[11px] font-bold uppercase text-slate-200 hover:bg-[#2563eb] hover:text-white transition-all`}
          >
            <LogOut size={16} />
            {!collapsed && 'Logout'}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;